import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhoneAlt, FaSearch } from 'react-icons/fa';

import HeaderCard from '../../components/Header/HeaderCard';
import { apiService } from '../../components/Config/api.service';
import FicheCollaborateur, { Avatar, sousTitreDe } from './FicheCollaborateur';

/**
 * L'annuaire interne (YRH-NEW-009-T2, Yros/Kavero#199), commun à l'espace
 * salarié et aux comptes internes.
 *
 * Le serveur borne tout : la société, le périmètre, le statut de présence et
 * les champs rendus (Yros/Kavero#493). L'écran n'affiche rien d'autre et ne
 * recalcule rien. La pastille a trois états et se lit aussi en texte
 * (`title`, `aria-label`) : la couleur seule ne suffit pas.
 *
 * Les filtres vivent dans l'adresse et ne s'appliquent qu'au clic sur
 * « Rechercher » (ou Entrée) ; changer de page recharge tout de suite. Pas de
 * rafraîchissement automatique de la présence.
 *
 * Compétences (phase 2, Yros/Kavero#486) : trois au plus sur la carte, toutes
 * avec leur niveau dans la fiche. La saisie libre est conservée : le champ
 * propose le catalogue, mais le serveur cherche en partiel, sans casse.
 *
 * `entete` : la coquille des comptes internes n'a pas de titre de page, l'espace
 * salarié en pose déjà un.
 */

const PAR_PAGE = 24;
const COMPETENCES_SUR_CARTE = 3;

// L'adresse de l'écran (`nom`, `competence`, `departement`, `presents`, `page`)
// vers la query string de la route dédiée du proxy (`nom`, `competence`,
// `departement_id`, `presents_uniquement`, `offset`, `limit`).
const filtresDe = (params) => ({
  nom: params.get('nom') || '',
  competence: params.get('competence') || '',
  departement: params.get('departement') || '',
  presents: params.get('presents') === '1',
});

const pageDe = (params) => Math.max(1, parseInt(params.get('page'), 10) || 1);

const requeteDe = (params) => {
  const { nom, competence, departement, presents } = filtresDe(params);
  const requete = new URLSearchParams();
  if (nom) requete.set('nom', nom);
  if (competence) requete.set('competence', competence);
  if (departement) requete.set('departement_id', departement);
  if (presents) requete.set('presents_uniquement', 'true');
  requete.set('offset', String((pageDe(params) - 1) * PAR_PAGE));
  requete.set('limit', String(PAR_PAGE));
  return requete.toString();
};

function Coordonnees({ collaborateur }) {
  if (!collaborateur.email && !collaborateur.telephone) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
      {collaborateur.email && (
        <a href={`mailto:${collaborateur.email}`} className="inline-flex items-center gap-1.5 text-[--color-primary] hover:underline min-w-0">
          <FaEnvelope className="shrink-0" aria-hidden="true" />
          <span className="truncate">{collaborateur.email}</span>
        </a>
      )}
      {collaborateur.telephone && (
        <a href={`tel:${collaborateur.telephone}`} className="inline-flex items-center gap-1.5 text-[--color-primary] hover:underline">
          <FaPhoneAlt className="shrink-0" aria-hidden="true" />
          <span>{collaborateur.telephone}</span>
        </a>
      )}
    </div>
  );
}

function CompetencesCarte({ competences }) {
  if (!competences?.length) return null;
  const autres = competences.length - COMPETENCES_SUR_CARTE;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {competences.slice(0, COMPETENCES_SUR_CARTE).map((nom) => (
        <li key={nom} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{nom}</li>
      ))}
      {autres > 0 && <li className="px-2 py-0.5 text-xs text-slate-500">{`+${autres}`}</li>}
    </ul>
  );
}

// Toutes les cartes ont la même hauteur : les coordonnées s'alignent en pied.
function Carte({ collaborateur, onOuvrir }) {
  const { t } = useTranslation();
  const sousTitre = sousTitreDe(collaborateur);
  return (
    <li className="h-full flex flex-col gap-3 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
      <button type="button" onClick={() => onOuvrir(collaborateur.id)}
        className="flex w-full items-center gap-3 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[--color-primary]">
        <Avatar collaborateur={collaborateur} />
        <span className="min-w-0">
          <span className="block font-semibold text-slate-800 truncate">{collaborateur.nom}</span>
          {sousTitre
            ? <span className="block text-sm text-slate-500 truncate">{sousTitre}</span>
            : <span className="block text-sm text-slate-400 italic">{t('annuaire.nonRenseigne')}</span>}
        </span>
      </button>
      <CompetencesCarte competences={collaborateur.competences} />
      <div className="mt-auto">
        <Coordonnees collaborateur={collaborateur} />
      </div>
    </li>
  );
}

function Annuaire({ entete = false }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [saisie, setSaisie] = useState(() => filtresDe(params));
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [ouvert, setOuvert] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const requete = requeteDe(params);
  const page = pageDe(params);

  // Les compétences connues, pour l'autocomplétion ; sans elles, le champ reste libre.
  useEffect(() => {
    let actif = true;
    apiService.lireCatalogueCompetences()
      .then((reponse) => {
        if (actif && reponse?.ok) setCatalogue((reponse.competences?.competences || []).map(({ nom }) => nom));
      })
      .catch(() => {});
    return () => { actif = false; };
  }, []);

  // Retour du navigateur ou lien partagé : le formulaire suit l'adresse.
  useEffect(() => { setSaisie(filtresDe(params)); }, [params]);

  useEffect(() => {
    let actif = true;
    setErreur(false);
    apiService.lireAnnuaire(requete)
      .then((reponse) => {
        if (!actif) return;
        if (reponse?.ok) setResultat(reponse);
        else setErreur(true);
      })
      .catch(() => { if (actif) setErreur(true); });
    return () => { actif = false; };
  }, [requete]);

  const rechercher = (evenement) => {
    evenement.preventDefault();
    const suivants = new URLSearchParams();
    if (saisie.nom.trim()) suivants.set('nom', saisie.nom.trim());
    if (saisie.competence.trim()) suivants.set('competence', saisie.competence.trim());
    if (saisie.departement) suivants.set('departement', saisie.departement);
    if (saisie.presents) suivants.set('presents', '1');
    setParams(suivants);
  };

  const allerA = (numero) => {
    const suivants = new URLSearchParams(params);
    if (numero > 1) suivants.set('page', String(numero));
    else suivants.delete('page');
    setParams(suivants);
  };

  const total = resultat?.pagination?.total_count || 0;
  const pages = Math.max(1, Math.ceil(total / PAR_PAGE));
  const collaborateurs = resultat?.collaborateurs || [];

  return (
    <section className="space-y-4">
      {entete && <HeaderCard title={t('annuaire.titre')} text={t('annuaire.description')} />}
      <form onSubmit={rechercher}
        className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-wrap items-end gap-3">
        <label className="grow basis-full sm:basis-60 min-w-0 text-sm text-slate-600">
          <span className="block mb-1">{t('annuaire.filtres.nom')}</span>
          <input type="search" value={saisie.nom} maxLength={100}
            onChange={(e) => setSaisie({ ...saisie, nom: e.target.value })}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-800" />
        </label>
        <label className="grow basis-full sm:basis-48 min-w-0 text-sm text-slate-600">
          <span className="block mb-1">{t('annuaire.filtres.competence')}</span>
          <input type="search" value={saisie.competence} maxLength={60} list="annuaire-competences"
            onChange={(e) => setSaisie({ ...saisie, competence: e.target.value })}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-800" />
          <datalist id="annuaire-competences">
            {catalogue.map((nom) => (
              <option key={nom} value={nom} data-testid="annuaire-competence-option" />
            ))}
          </datalist>
        </label>
        <label className="grow sm:grow-0 basis-full sm:basis-56 text-sm text-slate-600">
          <span className="block mb-1">{t('annuaire.filtres.departement')}</span>
          <select value={saisie.departement}
            onChange={(e) => setSaisie({ ...saisie, departement: e.target.value })}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-800 bg-white">
            <option value="">{t('annuaire.filtres.tousDepartements')}</option>
            {(resultat?.departements || []).map(({ id, nom }) => (
              <option key={id} value={String(id)}>{nom}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
          <input type="checkbox" checked={saisie.presents}
            onChange={(e) => setSaisie({ ...saisie, presents: e.target.checked })}
            className="accent-[--color-primary]" />
          <span>{t('annuaire.filtres.presents')}</span>
        </label>
        <button type="submit"
          className="ml-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[--color-primary] text-white text-sm font-medium hover:bg-[--bg-hover] transition-colors">
          <FaSearch aria-hidden="true" />
          {t('annuaire.rechercher')}
        </button>
      </form>

      {erreur && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t('annuaire.erreur')}
        </p>
      )}
      {!erreur && resultat === null && (
        <p className="text-sm text-slate-500">{t('annuaire.chargement')}</p>
      )}
      {!erreur && resultat && collaborateurs.length === 0 && (
        <p className="rounded-xl bg-white border border-slate-100 p-4 text-sm text-slate-500">{t('annuaire.vide')}</p>
      )}
      {!erreur && collaborateurs.length > 0 && (
        <>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] auto-rows-fr gap-3">
            {collaborateurs.map((collaborateur) => (
              <Carte key={collaborateur.id} collaborateur={collaborateur} onOuvrir={setOuvert} />
            ))}
          </ul>
          <nav className="flex items-center justify-between gap-3 text-sm text-slate-600"
            aria-label={t('annuaire.pagination')}>
            <button type="button" disabled={page <= 1} onClick={() => allerA(page - 1)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">
              {t('annuaire.precedent')}
            </button>
            <span>{t('annuaire.page', { page, pages, total })}</span>
            <button type="button" disabled={page >= pages} onClick={() => allerA(page + 1)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">
              {t('annuaire.suivant')}
            </button>
          </nav>
        </>
      )}

      {ouvert !== null && (
        <FicheCollaborateur employeId={ouvert} onOuvrir={setOuvert} onFermer={() => setOuvert(null)} />
      )}
    </section>
  );
}

export default Annuaire;
